import { ChevronDown, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatedCheckmark, HearthGlow } from '@/components/hearth'

interface PagePeaceStateProps {
  /** Icon to display in place of the animated checkmark */
  icon?: LucideIcon
  /** Primary heading text */
  title: string
  /** Secondary description text */
  description: string
  /** Callback when "View details" is clicked */
  onViewDetails?: () => void
  /** Whether details are expanded */
  detailsExpanded?: boolean
  /** Label for the toggle button */
  detailsLabel?: string
  /** Color variant */
  variant?: 'success' | 'primary'
  className?: string
}

/**
 * Page-level peace state (Health / Financial / Alerts): a floating Hearth
 * card with a soft bloom, drawn-on checkmark (or domain icon), and a serif
 * reassurance headline.
 */
export function PagePeaceState({
  icon: Icon,
  title,
  description,
  onViewDetails,
  detailsExpanded = false,
  detailsLabel = 'View details',
  variant = 'success',
  className,
}: PagePeaceStateProps) {
  const glowColor =
    variant === 'primary' ? 'rgb(231 240 235 / 0.9)' : 'rgb(220 238 227 / 0.85)'

  return (
    <div
      className={cn(
        'hearth-card-floating relative flex flex-col items-center justify-center overflow-hidden px-6 py-10 text-center',
        className
      )}
    >
      <HearthGlow color={glowColor} />
      <div className="relative flex flex-col items-center">
        {Icon ? (
          <div
            className={cn(
              'flex h-16 w-16 items-center justify-center rounded-full',
              variant === 'primary' ? 'bg-primary-100' : 'bg-success-100'
            )}
          >
            <Icon
              className={cn(
                'h-8 w-8',
                variant === 'primary' ? 'text-primary-600' : 'text-success-600'
              )}
            />
          </div>
        ) : (
          <AnimatedCheckmark size={64} />
        )}

        <h2
          className={cn(
            'mt-4 font-serif text-xl font-semibold',
            variant === 'primary' ? 'text-primary-800' : 'text-success-800'
          )}
        >
          {title}
        </h2>

        <p className="mt-2 max-w-md text-[15px] text-gray-500">{description}</p>

        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
          >
            <span>{detailsExpanded ? 'Hide details' : detailsLabel}</span>
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', detailsExpanded && 'rotate-180')}
            />
          </button>
        )}
      </div>
    </div>
  )
}
